import { Box, Divider, Stack } from '@mui/material';
import React from 'react';
import { Article, ArticleContent } from '../../../models/Article';
import ArticleCard from '../../ArticleCard';
import { StyledButton, StyledButtonGroup } from '../../styles/StyledButtons';
import StyledSection from '../../styles/StyledSection';
import { StyledHeading } from '../../styles/styles';
import ChartDataGroup from '../../template/ChartDataGroup';
import ImageCarousel from '../../template/ImageCarousel';
import LeftRightList from '../../template/LeftRightList';
import StandardHeaderText from '../../template/StandardHeaderText';
import Subtitle from '../../template/Subtitle';
import Table from '../../template/Table';
import TextBlock from '../../template/TextBlock';
import HeroImage from '../../template/HeroImage';
import ArticleApprovalStatusButtons from './ArticleApprovalStatusButtons';

interface ArticleApprovalPreviewProps {
  article: Article;
  content: ArticleContent;
  isProcessing: boolean;
  hasRunningJob: boolean;
  onDownloadOriginal: () => void;
  onPublish: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onRegenerate: () => void;
}

export default function ArticleApprovalPreview({
  article,
  content,
  isProcessing,
  hasRunningJob,
  onDownloadOriginal,
  onPublish,
  onArchive,
  onUnarchive,
  onRegenerate,
}: ArticleApprovalPreviewProps) {
  return (
    <Box sx={{ px: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <StyledHeading>Article Preview</StyledHeading>
        <StyledButton onClick={onDownloadOriginal}>Original Article</StyledButton>
      </Box>

      <StyledSection tone="tone1" mb="4">
        <StyledHeading headerVariant="heading2" sx={{ mb: 2 }}>
          Article Card Preview
        </StyledHeading>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ArticleCard tone="tone1" content={content} linkTo={`articles/${article.id}`} />
        </Box>
      </StyledSection>

      <Divider sx={{ my: 4 }} />
      <StyledHeading>Full Article Content</StyledHeading>

      <StyledSection tone="tone1" mb="4">
        {content.imageCarousel && (
          <ImageCarousel
            modelId={article.id}
            modelType="Article"
            {...content.imageCarousel.properties}
            editMode={false}
          />
        )}
        {content.headline && (
          <StandardHeaderText
            key={content.headline.id}
            {...content.headline.properties}
            editMode={false}
          />
        )}
      </StyledSection>

      <Divider sx={{ my: 4 }} />
      <StyledHeading>Extended Summary</StyledHeading>
      <StyledSection tone="tone1" mb="4">
        <Stack spacing={3}>
          {content.extendedSummary.map((block) => {
            switch (block.type) {
              case 'StandardHeaderText':
                return <StandardHeaderText key={block.id} {...block.properties} editMode={false} />;
              case 'Subtitle':
                return <Subtitle key={block.id} id={block.id} {...block.properties} />;
              case 'Text':
                return <TextBlock key={block.id} id={block.id} {...block.properties} />;
              case 'HeroImage':
                return <HeroImage key={block.id} id={block.id} {...block.properties} />;
              case 'ImageCarousel':
                return (
                  <ImageCarousel
                    key={block.id}
                    modelId={article.id}
                    modelType="Article"
                    {...block.properties}
                    editMode={false}
                  />
                );
              case 'LeftRightList':
                return <LeftRightList key={block.id} {...block.properties} editMode={false} />;
              case 'Table':
                return <Table key={block.id} {...block.properties} editMode={false} />;
              case 'ChartData':
                return (
                  <ChartDataGroup
                    key={block.id}
                    charts={block.properties.charts || []}
                    tone="tone1"
                    footnotes={block.properties.footnotes}
                  />
                );
              default:
                return null;
            }
          })}
        </Stack>
      </StyledSection>

      <StyledButtonGroup>
        <ArticleApprovalStatusButtons
          article={article}
          isProcessing={isProcessing}
          hasRunningJob={hasRunningJob}
          onPublish={onPublish}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onRegenerate={onRegenerate}
        />
      </StyledButtonGroup>
    </Box>
  );
}
